import { EventSyncChange, JobWork, StoreExecutor } from "@project/shared-types";
import { produce } from "immer";
import { createContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { StoreGetFullAPI, StoreSyncAPI } from "../../api/StoreAPI";
import { useLocalStorage } from "../../hooks/useLocalStoreg";
import useWebSocketKubik from "../../hooks/useWebSocketKubik";
import { Logger } from "../../tools/debugLogger";
import { useUserId } from "../user/useUserId";

export const StoreExecutorContext = createContext<{
  dataExecutor: StoreExecutor;
  isLoading: boolean;
  fastUpdateJobWithWork: (job: JobWork) => void;
} | null>(null);

export const StoreExecutorProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const logger = new Logger(undefined, true);
  const userId = useUserId();
  const [isLoading, setIsLoading] = useState(false);

  // 1. Хранение данных
  const [dataExecutor, setDataExecutor] = useLocalStorage<StoreExecutor>(
    "dataExecutor",
    {} as StoreExecutor
  );
  const { isConnected, subscribeWebSocket, unsubscribeWebSocket } =
    useWebSocketKubik();
  const location = useLocation();


  const fastUpdateJobWithWork = (job: JobWork) => {
    setDataExecutor(
      produce((draft) => {
        draft.jobs[job.job.id] = job.job;
        if (job.work) {
          draft.works[job.work.id] = job.work;
        }
      })
    );
  };
  // 2. Полная загрузка данных
  const loadFullData = async () => {
    if (!userId && isConnected) return;
    setIsLoading(true);

    try {
      const response = await StoreGetFullAPI({ current_url: location.pathname });
      if (response) {
        setDataExecutor(response.map);
        setIsLoading(false);
      }
    } catch (error) {
      logger.error("Ошибка при загрузке полных данных:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFullData();
  }, [userId]);
  // 3. Обновление изменённых данных
  const syncRecord = async (event: EventSyncChange) => {
    logger.log("WebSocket: Received transactions:", event);
    setIsLoading(true);
    const response = await StoreSyncAPI(event);
    if (response) {
      setDataExecutor(
        produce((draft) => {
          if (Object.keys(response.map.jobs).length > 0) {
            draft.jobs = { ...draft.jobs, ...response.map.jobs };
          }
          if (Object.keys(response.map.works).length > 0) {
            draft.works = { ...draft.works, ...response.map.works };
          }
          if (Object.keys(response.map.places).length > 0) {
            draft.places = { ...draft.places, ...response.map.places };
          }
        })
      );
    }
    setIsLoading(false);
  };
  //Отслеживаем сообщения от сервера, если в текущем workspace что-то меняется
  useEffect(() => {
    if (!isConnected) return;
    // Подписываемся на уведомления о транзакциях
    const handleSyncNotification = (newTransactions: EventSyncChange) => {
      logger.log("WebSocket: Received transactions:", newTransactions);
      syncRecord(newTransactions);
    };

    subscribeWebSocket("notify_transaction", handleSyncNotification);

    // Отключаем WebSocket при размонтировании компонента
    return () => {
      unsubscribeWebSocket("notify_transaction", handleSyncNotification);
    };
  }, [userId, isConnected]);

  return (
    <StoreExecutorContext.Provider
      value={{
        dataExecutor,
        isLoading,
        fastUpdateJobWithWork
      }}
    >
      {children}
    </StoreExecutorContext.Provider>
  );
};
